import React, { useMemo } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";

import { getNetTotalEnergyConsumption } from "utils/results";
import { calcOperatingCosts } from "utils/upgrades";

import { selectResults } from "features/Model/Review/Results/_ducks/selectors";
import { selectNzStudyReportPdfData, selectModelData, selectFuelPrices } from "features/Model/_ducks/selectors";
import { selectAllImages } from "store/imageGallery/selectors";
import { selectUpgradePackages } from "features/Model/Upgrades/_ducks/selectors";

import NZReportPDFPage1 from "./NZReportPDFPage1";
import NZReportPDFPage2 from "./NZReportPDFPage2";
import NZReportPDFPage3 from "./NZReportPDFPage3";
import NZReportPDFPage4 from "./NZReportPDFPage4";
import NZReportPDFPageImages from "./NZReportPDFImagePage";
import NZReportPDFPageDetails from "./NZReportPDFDetailsPage";

const NZReportPDF = ({
    pdfRef,
    nzStudyReportPdfData,
    allImages,
    results,
    modelData,
    upgradePackages,
    fuelPrices,
    eFileModelData = {},
    eFileResults = {},
    eFileUpgradePackages = {},
}) => {
    const {
        selectedUpgrade = "",
        projectIntroduction = "",
        projectPriorities = [""],
        beforeComfort = "",
        afterComfort = "",
        beforeBuildingCondition = "",
        afterBuildingCondition = "",
        beforeElectricalPanelSize = "",
        afterElectricalPanelSize = "",
        estimatedUpfront = 0,
        carbonPayback = 0,
        operatingCostSaving: storedOperatingCostSaving = 0,
        operatingCostSavingOverride = false,
        whyAreWeRetrofittingOurHome = "",
        whatLessonsDidWeLearn = "",
        whatElseNeedsToBeDone = "",
        preConstructionImages = [],
        midConstructionImages = [],
        postConstructionImages = [],
        technicalDetailsImages = [],
        pdfCoverImage = [],
        nzPage1Content = {},
        nzUpgradesIncluded = [],
        nzPage2Content = {},
    } = nzStudyReportPdfData;

    // Determine if selected upgrade is from E-file
    const isEFileUpgrade = selectedUpgrade.startsWith("eFile_");
    const actualUpgradeKey = isEFileUpgrade ? selectedUpgrade.replace("eFile_", "") : selectedUpgrade;

    const upgradeCaseKey = isEFileUpgrade && actualUpgradeKey === "base" ? "socBaseCase" : "socAllUpgrades";

    // Use E-file data if selected upgrade is from E-file, otherwise use current model data
    const postRetrofitPackageResults = isEFileUpgrade ? eFileResults : results?.packageResults || {};
    const postRetrofitModelData = isEFileUpgrade ? eFileModelData : modelData;
    const postRetrofitUpgradePackages = isEFileUpgrade ? eFileUpgradePackages : upgradePackages;

    const { modelData: preRetrofitModelDataFromResults = {}, resultsData: preRetrofitResultsData = {} } =
        results?.packageResults?.base || {};

    const { modelData: postRetrofitModelDataFromResults = {}, resultsData: postRetrofitResultsData = {} } =
        postRetrofitPackageResults[actualUpgradeKey] || {};

    //all of this comes from the pre retrofit file
    const {
        chbaNz: { file: { renovatorCompany = "", targetLabel = {}, targetCompletionDate = "" } = {} } = {}, //pre retrofit file
        h2kVersion: { major = 0, minor = 0 } = {}, //pre retrofit file
        components = {}, //pre retrofit file
        airTightness = {}, //pre retrofit file
        codes = {}, //pre retrofit file
    } = modelData || {};

    const performanceId = targetLabel.id ?? 0; //pre retrofit file

    const {
        specifications: {
            yearBuilt: { value: yearBuilt = "" } = {}, //pre retrofit file
            buildingType: { id: buildingTypeId = 0 } = {}, //pre retrofit file
        } = {},
        location: {
            //pre retrofit file
            weather: {
                region: { id: regionId = 0 } = {},
                location: {
                    // Location
                    id: locationId = 0,
                    // HDD
                    value: heatingDegreeDay = 0,
                } = {},
            } = {},
        } = {},
        uiSettings: { primaryUnits = "Metric" } = {},
        domesticHotWater = {}, //pre retrofit file
        heatingCooling = {}, //pre retrofit file
        ventilation = {}, //pre retrofit file
        generation = {}, //pre retrofit file
        baseLoads = {}, //pre retrofit file
        program = {}, //pre retrofit file
    } = modelData || {};

    const {
        dimensions: {
            aboveGradeIntFloorArea: { total: aboveGradeIntFloorAreaTotal = 0 } = {}, //post retrofit file
            belowGradeIntFloorArea: { total: belowGradeIntFloorAreaTotal = 0 } = {}, //post retrofit file
        } = {},
    } = postRetrofitModelDataFromResults || {};

    const energyReduction = useMemo(() => {
        const { zeroFloorTotal: netTotalConsumption = 0 } = getNetTotalEnergyConsumption(
            postRetrofitResultsData[upgradeCaseKey] || {}
        );
        const { zeroFloorTotal: netBaseConsumption = 0 } = getNetTotalEnergyConsumption(
            preRetrofitResultsData["socBaseCase"] || {}
        );

        return ((100 * (netBaseConsumption - netTotalConsumption)) / netBaseConsumption).toFixed(1);
    }, [preRetrofitResultsData, postRetrofitResultsData]);

    const operatingCostSaving = useMemo(() => {
        // If override is true, use the stored value; otherwise calculate it
        if (operatingCostSavingOverride) {
            return storedOperatingCostSaving;
        }

        const operatingExpenses = calcOperatingCosts(postRetrofitResultsData[upgradeCaseKey], fuelPrices, "total");
        const baseOperatingExpenses = calcOperatingCosts(preRetrofitResultsData["socBaseCase"], fuelPrices, "total");

        return baseOperatingExpenses - operatingExpenses;
    }, [
        fuelPrices,
        preRetrofitResultsData,
        postRetrofitResultsData,
        operatingCostSavingOverride,
        storedOperatingCostSaving,
    ]);

    return (
        <div ref={pdfRef} id="nzPdfPrint">
            <NZReportPDFPage1
                projectIntroduction={projectIntroduction}
                projectPriorities={projectPriorities}
                images={pdfCoverImage}
                allImages={allImages}
                nzPage1Content={nzPage1Content} // changes to autofilled inputs
                nzUpgradesIncluded={nzUpgradesIncluded} // changes to autofilled upgrades
            />
            <NZReportPDFPage2
                beforeComfort={beforeComfort}
                afterComfort={afterComfort}
                beforeBuildingCondition={beforeBuildingCondition}
                afterBuildingCondition={afterBuildingCondition}
                beforeElectricalPanelSize={beforeElectricalPanelSize}
                afterElectricalPanelSize={afterElectricalPanelSize}
                nzPage2Content={nzPage2Content} // changes to autofilled inputs
            />
            <NZReportPDFPage3
                preRetrofitModelData={preRetrofitModelDataFromResults}
                postRetrofitModelData={postRetrofitModelDataFromResults}
                preRetrofitResultsData={preRetrofitResultsData}
                postRetrofitResultsData={postRetrofitResultsData}
                energyReduction={energyReduction}
                operatingCostSaving={operatingCostSaving}
                estimatedUpfront={estimatedUpfront}
                carbonPayback={carbonPayback}
                eFileBaseModelData={isEFileUpgrade && actualUpgradeKey === "base" ? eFileModelData : {}}
            />
            {(whyAreWeRetrofittingOurHome || whatLessonsDidWeLearn || whatElseNeedsToBeDone) && (
                <NZReportPDFPage4
                    whyAreWeRetrofittingOurHome={whyAreWeRetrofittingOurHome}
                    whatLessonsDidWeLearn={whatLessonsDidWeLearn}
                    whatElseNeedsToBeDone={whatElseNeedsToBeDone}
                />
            )}
            {preConstructionImages.length > 0 && (
                <NZReportPDFPageImages imagesType="BEFORE" images={preConstructionImages} allImages={allImages} />
            )}
            {midConstructionImages.length > 0 && (
                <NZReportPDFPageImages imagesType="DURING" images={midConstructionImages} allImages={allImages} />
            )}
            {postConstructionImages.length > 0 && (
                <NZReportPDFPageImages imagesType="AFTER" images={postConstructionImages} allImages={allImages} />
            )}
            {technicalDetailsImages.map((imageId, index) => {
                const imageData = allImages.find((img) => `${img.id}-detail` === imageId);

                return (
                    <NZReportPDFPageDetails
                        key={`technical-detail-page-${index}`}
                        imageData={imageData}
                        index={index}
                    />
                );
            })}
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    nzStudyReportPdfData: selectNzStudyReportPdfData,
    allImages: selectAllImages,
    results: selectResults,
    modelData: selectModelData,
    upgradePackages: selectUpgradePackages,
    fuelPrices: selectFuelPrices,
});

export default connect(mapStateToProps)(NZReportPDF);
